import { shippingRatesByFromLocation } from ".";
import { SALE_CHANNEL } from "../salesChannels";
import { SupplierName } from "../suppliersConfig";
import { OriginShippingCountries, ShippingRate, ShippingService } from "./shippingRates.types";
import Big from "big.js";


export function filterShippingService({
  orderPlatform,
  supplier,
  destination,
  packageSize1,
  packageSize2,
  packageSize3,
  packageWeight,
}: {
  orderPlatform: SALE_CHANNEL,
  supplier: SupplierName,
  destination?: string,
  packageSize1: number,
  packageSize2: number,
  packageSize3: number,
  packageWeight: number,
}) {

  return (service: ShippingService) => (
    // Only coming from order platform
    (service.conditions?.orderPlatform === undefined || service.conditions?.orderPlatform === orderPlatform)
    // Only from specific supplier
    && (service.conditions?.supplier === undefined || service.conditions?.supplier?.includes(supplier))
    // Can fulfill to destination, or for any destination if no destination is specified
    && (destination === undefined || service.ratesByDestination.some(ratesByDestination => (
      ratesByDestination.destinations.includes(destination)
    )))
    // Max dimensions not exceeded
    && service.maxDimensions(packageSize1, packageSize2, packageSize3, packageWeight).isValid
  )
}

export function getShippingServicesForPackage({
  onlyServices,
  packageWeight,
  packageSize1,
  packageSize2,
  packageSize3,
  orderPlatform,
  supplier,
  origin,
  destination,
}: {
  onlyServices?: ShippingService[],
  packageWeight: number,
  packageSize1: number,
  packageSize2: number,
  packageSize3: number,
  orderPlatform: SALE_CHANNEL,
  supplier: SupplierName,
  origin: OriginShippingCountries,
  destination: string,
}): { service: ShippingService, rate: ShippingRate }[] {
  const possibleShippingServices = (onlyServices ?? shippingRatesByFromLocation[origin]).filter(
    filterShippingService({
      orderPlatform,
      supplier,
      destination,
      packageSize1,
      packageSize2,
      packageSize3,
      packageWeight,
    })
  );

  const shippingServices = possibleShippingServices.reduce<{ service: ShippingService, rate: ShippingRate }[]>((acc, service) => {
    const volumetricWeight = (
      new Big(packageSize1 * packageSize2 * packageSize3).div(service.volumetricWeightDivider || 5000).times(1000)
    );

    const weightToUse = (
      service.appliesVolumetricWeight
    )
      ? volumetricWeight.gt(packageWeight) ? volumetricWeight.round(0, Big.roundUp).toNumber() : packageWeight
      : packageWeight;

    const ratesForDestination = service.ratesByDestination.find(rate => (
      rate.destinations.includes(destination)
    ));

    if (!ratesForDestination) {
      throw new Error(`No rates for destination ${destination} for service ${service.carrier}, should not happen her`);
    }

    const rate = ratesForDestination.rates.find(rate => (
      rate.minWeight <= weightToUse && rate.maxWeight >= weightToUse
    ));

    if (!rate) {
      console.log(`No rate found on shipping service`, {
        service,
        weightToUse,
        rates: ratesForDestination.rates,
      })
      return acc;
    }

    acc.push({
      service,
      rate,
    });
    return acc;
  }, []);

  return shippingServices.sort((a, b) => a.rate.priceWithVAT - b.rate.priceWithVAT);
}